<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 内容文本 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        内容文本 <span class="text-red-500">*</span>
      </label>
      <input v-model="formData.text" type="text" required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如：abandon" />
    </div>

    <!-- 内容类型和语言 -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          内容类型 <span class="text-red-500">*</span>
        </label>
        <select v-model="formData.content_type" required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200">
          <option :value="1">单词</option>
          <option :value="2">句子</option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          语言 <span class="text-red-500">*</span>
        </label>
        <select v-model="formData.language_id" required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200">
          <option :value="1">英语</option>
          <option :value="2">中文</option>
        </select>
      </div>
    </div>

    <!-- 音标 -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">音标 (UK)</label>
        <input v-model="formData.ipa_uk" type="text"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
          placeholder="例如：/əˈbændən/" />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">音标 (US)</label>
        <input v-model="formData.ipa_us" type="text"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
          placeholder="例如：/əˈbændən/" />
      </div>
    </div>

    <!-- 翻译 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">翻译</label>
      <textarea v-model="formData.translation" rows="3"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如：v. 放弃；抛弃；遗弃"></textarea>
    </div>

    <!-- 音节划分 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">音节划分</label>
      <input v-model="formData.hyphenation" type="text"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        placeholder="例如：a·ban·don" />
    </div>

    <!-- 提交按钮 -->
    <div class="flex justify-end gap-3 pt-4">
      <button type="button"
        class="cancel-btn text-sm font-medium"
        @click="$emit('cancel')">
        取消
      </button>
      <button type="submit" :disabled="loading"
        class="primary-btn text-sm font-medium disabled:opacity-50">
        <span v-if="loading"
          class="inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
        {{ loading ? '保存中...' : '保存' }}
      </button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import type { ContentCreateRequest } from '@/api/dictation/content';

/**
 * 内容表单组件
 *
 * @example
 * <ContentForm
 *   :initial-data="content"
 *   :loading="loading"
 *   @submit="handleSubmit"
 *   @cancel="handleCancel"
 * />
 */

interface Props {
  /** 初始数据（编辑模式） */
  initialData?: Partial<ContentCreateRequest>;
  /** 加载状态 */
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
});

const emit = defineEmits<{
  'submit': [data: any];
  'cancel': [];
}>();

// 表单数据
const formData = ref<ContentCreateRequest>({
  text: '',
  content_type: 1,
  language_id: 1,
  ipa_uk: '',
  ipa_us: '',
  translation: '',
  hyphenation: '',
});

// 初始化表单数据
if (props.initialData) {
  Object.assign(formData.value, props.initialData);
}

// 监听初始数据变化
watch(() => props.initialData, (newData) => {
  if (newData) {
    Object.assign(formData.value, newData);
  }
}, { deep: true });

// 提交表单
const handleSubmit = () => {
  emit('submit', { ...formData.value });
};
</script>
